import React from 'react'
import { ManufacturerState } from '../../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, Typography } from '@mui/material'
import { UnitTypes } from 'src/Libs/types'
import { isEmpty, isString } from 'lodash'
import { InformationTile, WarningTile } from '../messages'
import { UserActionRequiredErrorDto } from '../../dto'
import UserActionRequiredTile from './UserActionRequiredTile'

interface Props {
  vehicleAvailable: boolean
  isVehicleInformationAvailable: boolean
  estimatedRange?: number
  stateOfCharge?: number
  vendorState?: ManufacturerState
  isVehicleConnectionInProgress: boolean
  isVehicleDisconnected: boolean
  vin: string
  userActionRequiredError: UserActionRequiredErrorDto[] | null
}

/**
 * Displays vehicle information such as estimated range and state of charge.
 * Shows appropriate status messages and icons when the vehicle or its information is unavailable.
 *
 * @param vehicleAvailable - Indicates if the vehicle is available.
 * @param isVehicleInformationAvailable - Indicates if vehicle information is available.
 * @param estimatedRange - The estimated range of the vehicle in kilometers.
 * @param stateOfCharge - The state of charge of the vehicle in percentage.
 * @param vendorState - The current state of the vehicle from the manufacturer.
 * @param isVehicleConnectionInProgress - Indicates if the vehicle connection is in progress (waiting for data from server, might take a few mins).
 * @param isVehicleDisconnected - Indicates if the vehicle is disconnected.
 * @param vin - The vehicle identification number.
 * @param userActionRequiredError - List of errors that require user action.
 *
 * @returns A React element displaying vehicle information or status messages.
 */
const VehicleInformation: React.FC<Props> = ({
  vehicleAvailable,
  isVehicleInformationAvailable,
  estimatedRange,
  stateOfCharge,
  vendorState,
  isVehicleConnectionInProgress,
  isVehicleDisconnected,
  vin,
  userActionRequiredError,
}) => {
  const { t } = useCustomTranslation()
  const isTemporarilyUnavailable = vendorState === ManufacturerState.temporarilyUnavailable
  const isManufacturerTemporaryUnavailable = !vehicleAvailable && isTemporarilyUnavailable
  const isManufacturerPermanentlyUnavailable = !vehicleAvailable && !isTemporarilyUnavailable
  const hasVin = isString(vin) && !isEmpty(vin)
  const hasUserActionRequiredError = userActionRequiredError && userActionRequiredError.length > 0

  if (isVehicleConnectionInProgress) {
    return <InformationTile message={t('emobility.vehicle-integration.vehicles.connection-in-progress')} />
  }

  if (isVehicleDisconnected && hasVin) {
    return <WarningTile message={t('vehicle-integration.disconnected-vehicle.reauthenticate-vehicle')} />
  }

  /* vehicles without VIN (eg. BMW) can not be re-authenticated */
  if (isVehicleDisconnected && !hasVin) {
    return <WarningTile message={t('vehicle-integration.disconnected-vehicle.remove-and-add-vehicle-again')} />
  }

  if (isManufacturerTemporaryUnavailable) {
    return <InformationTile message={t('emobility.vehicle-integration.vehicles.temporarily-unavailable')} />
  }

  if (isManufacturerPermanentlyUnavailable) {
    return <WarningTile message={t('emobility.vehicle-integration.vehicles.not-available')} />
  }

  /* We don't show all errors in the vehicle information tile */
  if (hasUserActionRequiredError) {
    return <UserActionRequiredTile userActionRequiredError={userActionRequiredError} showAllErrors={false} />
  }

  if (vehicleAvailable && isVehicleInformationAvailable) {
    return (
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack direction="row" alignItems="baseline">
          <Typography variant="h1">
            {stateOfCharge ?? '--'}
            <Typography variant="h2" component="span">{` ${UnitTypes.percentage}`}</Typography>
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="baseline">
          <Typography variant="h1">
            {estimatedRange ?? '--'}
            <Typography variant="h2" component="span">{` ${UnitTypes.kilometer}`}</Typography>
          </Typography>
        </Stack>
      </Stack>
    )
  }

  return null
}

export default VehicleInformation
